import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Users, UserPlus, Loader2, Trash2, Trophy, TrendingUp,
  AlertTriangle, BookOpen, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminBotsTab = ({ token }) => {
  const [botStats, setBotStats] = useState(null);
  const [botLoading, setBotLoading] = useState(false);
  const [botCount, setBotCount] = useState(100);
  const [showBotManual, setShowBotManual] = useState(false);
  const [tradingStatus, setTradingStatus] = useState(null);

  useEffect(() => {
    fetchBotStats();
    fetchTradingStatus();
  }, []);

  const fetchBotStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setBotStats(await res.json());
    } catch (err) {
      console.error('Error fetching bot stats:', err);
    }
  };

  const fetchTradingStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/trading-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTradingStatus(await res.json());
    } catch (err) {
      console.error('Error fetching trading status:', err);
    }
  };

  const createBots = async () => {
    setBotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/create?count=${botCount}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created} bots!`);
        fetchBotStats();
      } else toast.error('Failed to create bots');
    } catch { toast.error('Error creating bots'); }
    finally { setBotLoading(false); }
  };

  const runBotTrading = async (percent = 25) => {
    setBotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/trade?percent=${percent}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.trades_made} trades from ${data.bots_selected} bots (${data.already_traded_today} already traded today)`);
        fetchTradingStatus();
      } else toast.error('Failed to run bot trading');
    } catch { toast.error('Error running bot trading'); }
    finally { setBotLoading(false); }
  };

  const generateBotScores = async () => {
    setBotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/generate-scores`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const clearedInfo = data.old_scores_cleared
          ? ` (cleared ${data.old_scores_cleared.weekly || 0} old scores)`
          : '';
        if (data.bots_scored === 0) {
          const di = data.debug || {};
          const reason = di.no_portfolio > 0 ? `${di.no_portfolio} bots have no portfolio`
            : di.empty_holdings > 0 ? `${di.empty_holdings} bots have empty holdings`
            : di.score_returned_none > 0 ? `${di.score_returned_none} scoring returned null`
            : 'Unknown reason';
          toast.error(`No bots scored! Reason: ${reason}. Found ${di.bots_found || 0} bots.`);
        } else {
          toast.success(`Generated realistic scores for ${data.bots_scored} bots!${clearedInfo}`);
        }
        fetchBotStats();
      } else {
        const err = await res.json();
        toast.error(`Failed to generate scores: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) { toast.error('Error generating scores: ' + err.message); }
    finally { setBotLoading(false); }
  };

  const deleteAllBots = async () => {
    if (!confirm('Are you sure you want to delete ALL bots? This cannot be undone!')) return;
    setBotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/delete-all`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.users_deleted} bots`);
        setBotStats(null);
      } else toast.error('Failed to delete bots');
    } catch { toast.error('Error deleting bots'); }
    finally { setBotLoading(false); }
  };

  const debugBots = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/debug`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const debugText = `BOT DEBUG INFO:\n- Total users: ${data.total_users}\n- is_bot=true: ${data.is_bot_true}\n- Bot IDs found: ${data.bot_ids_count}\n- Portfolios: ${data.portfolios_for_bots}\n- Portfolios WITH holdings: ${data.portfolios_with_holdings}\n- Scores: ${data.scores_for_bots}\n- Instruments: ${data.instruments_count}\n- Season: ${data.current_season}`;
        try { await navigator.clipboard.writeText(debugText); toast.success('Debug info copied!'); }
        catch { prompt('Copy this debug info:', debugText); }
      }
    } catch { toast.error('Error getting debug info'); }
  };

  const fixBotPortfolios = async () => {
    if (!confirm('This will DELETE and REBUILD all bot portfolios. Continue?')) return;
    setBotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bots/fix-portfolios`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.error) toast.error(data.error);
        else {
          const scoringInfo = data.scoring?.bots_scored ? `Scored ${data.scoring.bots_scored} bots!` : 'Scoring: 0';
          toast.success(`Deleted ${data.deleted_old} old, created ${data.created_new} new portfolios. ${scoringInfo}`);
          fetchBotStats();
        }
      } else {
        const err = await res.json();
        toast.error(`Failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) { toast.error('Error fixing bots: ' + err.message); }
    finally { setBotLoading(false); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800 mb-8" data-testid="admin-bots-tab">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-400" />
          Virtual Users (Bots)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-6 flex-1">
            <div>
              <p className="text-2xl font-bold text-white">{botStats?.total_bots || 0}</p>
              <p className="text-xs text-slate-400">Total Bots</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{botStats?.bots_with_portfolios || 0}</p>
              <p className="text-xs text-slate-400">With Portfolios</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">{botStats?.bots_with_scores || 0}</p>
              <p className="text-xs text-slate-400">With Scores</p>
            </div>
            {botStats?.by_strategy && (
              <div className="hidden lg:block">
                <p className="text-xs text-slate-400 mb-1">Strategies</p>
                <div className="flex gap-1">
                  {Object.entries(botStats.by_strategy).slice(0, 4).map(([s, c]) => (
                    <Badge key={s} variant="outline" className="text-xs">{s}: {c}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="number" value={botCount} onChange={(e) => setBotCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 100)))} className="w-24 bg-slate-800 border-slate-700" min="1" max="500" />
            <Button onClick={createBots} disabled={botLoading} className="bg-purple-600 hover:bg-purple-500">
              {botLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Create Bots
            </Button>
            {botStats?.total_bots > 0 && (botStats?.bots_with_portfolios < botStats?.total_bots || botStats?.bots_with_scores === 0) && (
              <Button onClick={fixBotPortfolios} disabled={botLoading} className="bg-yellow-600 hover:bg-yellow-500">
                {botLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                Fix Bots
              </Button>
            )}
            <Button onClick={generateBotScores} disabled={botLoading || !botStats?.total_bots} className="bg-blue-600 hover:bg-blue-500">
              <Trophy className="h-4 w-4 mr-1" /> Generate Scores
            </Button>
            <Button onClick={runBotTrading} disabled={botLoading || !botStats?.total_bots} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10">
              <TrendingUp className="h-4 w-4 mr-1" /> Run Trading
            </Button>
            <Button onClick={deleteAllBots} disabled={botLoading || !botStats?.total_bots} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/10">
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button onClick={debugBots} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-300" title="Debug bot data">?</Button>
            <Button onClick={() => setShowBotManual(!showBotManual)} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Guide">
              <BookOpen className="h-4 w-4 mr-1" /> Guide
              {showBotManual ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          </div>
        </div>

        {tradingStatus && (
          <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-emerald-400" /> Automated Trading Schedule
                <Badge className={tradingStatus.is_trading_day ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                  {tradingStatus.is_trading_day ? 'Trading Day' : 'Weekend'}
                </Badge>
              </h4>
              <Button onClick={fetchTradingStatus} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {tradingStatus.schedule?.map((slot, idx) => (
                <div key={idx} className="bg-slate-900/50 rounded p-2 text-center">
                  <p className="text-sm font-medium text-white">{String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')} UTC</p>
                  <p className="text-xs text-slate-400">{slot.label}</p>
                  <p className="text-xs text-emerald-400 font-semibold">{slot.percent}% of bots</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-slate-900/30 rounded">
              <div className="text-center">
                <p className="text-lg font-bold text-white">{tradingStatus.today_stats?.total_bots || 0}</p>
                <p className="text-xs text-slate-400">Total Bots</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-400">{tradingStatus.today_stats?.bots_traded || 0}</p>
                <p className="text-xs text-slate-400">Traded Today</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-blue-400">{tradingStatus.today_stats?.bots_remaining || 0}</p>
                <p className="text-xs text-slate-400">Remaining</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-yellow-400">{tradingStatus.today_stats?.total_trades || 0}</p>
                <p className="text-xs text-slate-400">Total Trades</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div>
                {tradingStatus.next_scheduled_run && (
                  <span className="text-slate-400">
                    Next run: <span className="text-white">{tradingStatus.next_scheduled_run.label}</span>
                    {' at '}
                    <span className="text-emerald-400">
                      {new Date(tradingStatus.next_scheduled_run.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} UTC
                    </span>
                    <span className="text-slate-500 ml-1">({tradingStatus.next_scheduled_run.percent}%)</span>
                  </span>
                )}
              </div>
              <div className="text-slate-400 text-xs">Rule: Each bot trades max once/day</div>
            </div>
            {tradingStatus.recent_runs?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <p className="text-xs text-slate-500 mb-2">Recent Trading Runs:</p>
                <div className="flex flex-wrap gap-2">
                  {tradingStatus.recent_runs.slice(0, 5).map((run, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs border-slate-600">
                      {run.source?.replace('scheduled_', '').replace('_', ' ') || 'manual'}: {run.trades_made} trades
                      {run.percent_requested && <span className="text-slate-500 ml-1">({run.percent_requested}%)</span>}
                      <span className="text-slate-500 ml-1">({new Date(run.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {showBotManual && (
          <div className="mt-4 p-5 bg-slate-800/70 rounded-lg border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-400" /> Bot Management Guide
            </h3>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="text-emerald-400 font-medium mb-2">What are Bots?</h4>
                <p className="text-slate-300">Virtual users (bots) are AI-controlled players that populate your leaderboard, making the game feel active and competitive. Each bot has a unique trading strategy and skill level, and their scores are calculated using real market data.</p>
              </div>
              <div>
                <h4 className="text-emerald-400 font-medium mb-2">Button Reference</h4>
                <div className="space-y-2">
                  {[
                    { label: 'Create Bots', color: 'bg-purple-600', desc: 'Creates new bot users with portfolios and generates their scores.' },
                    { label: 'Fix Bots', color: 'bg-yellow-600', desc: 'Deletes all bot portfolios and recreates them with proper holdings, then recalculates scores.' },
                    { label: 'Generate Scores', color: 'bg-blue-600', desc: 'Recalculates all bot scores based on current market prices.' },
                    { label: 'Run Trading', color: 'border border-emerald-600 text-emerald-400', desc: 'Simulates trading activity for bots.' },
                  ].map(item => (
                    <div key={item.label} className="flex items-start gap-3 p-2 bg-slate-900/50 rounded">
                      <span className={`${item.color} text-white text-xs px-2 py-1 rounded font-medium whitespace-nowrap`}>{item.label}</span>
                      <p className="text-slate-300">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-purple-900/30 border border-purple-700/50 rounded">
                <h4 className="text-purple-300 font-medium mb-2">Quick Start</h4>
                <ol className="text-slate-300 space-y-1">
                  <li>1. Set bot count to 500-1000</li>
                  <li>2. Click <strong>"Create Bots"</strong></li>
                  <li>3. Check leaderboard</li>
                  <li>4. If empty, click <strong>"Fix Bots"</strong></li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {!botStats?.total_bots && (
          <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h4 className="text-yellow-400 font-medium mb-2">No bots in this database</h4>
            <p className="text-slate-400 text-sm mb-3">Follow these steps to populate the leaderboard with AI players:</p>
            <ol className="text-slate-300 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span><strong>Create Bots</strong> - Set number and click "Create Bots"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span><strong>Run Trading</strong> - Builds portfolios for each bot</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span><strong>Generate Scores</strong> - Calculate realistic scores</span>
              </li>
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
